import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { ChevronDown, ChevronRight, ShoppingCart, Plus, Trash2, Package, FileDown, Upload, Loader2, CheckCircle, X, Save, Eye, Mail, RotateCcw, MoreHorizontal, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericFormat } from "react-number-format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { btn } from "@/components/common/btn";
import { getPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, updatePOStatus, receivePOItems, parseVendorQuote, getManufacturers, getBillOfMaterials, generateAllPOs, acknowledgePurchaseOrder, getSettings, submitPOForApproval, approvePO } from "@/lib/api";
import { FoundationSyncPill } from "@/components/integrations/FoundationSyncPill";
import { consumeFocusTarget, scrollToTestId, FOCUS_PO } from "@/lib/deepLinkFocus";
import { MaterialToOrder } from "@/components/purchaseOrders/MaterialToOrder";
import { ReceivePOModal, remainingQty } from "@/components/purchasing/ReceivePOForm";
import { ShipmentDialog, TrackingLink } from "@/components/purchasing/ShipmentDialog";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { addCompanyHeader } from "@/lib/pdfHeader";
import { getEmailDocMode, docFilename } from "@/components/common/EmailDocMode";
import { downloadPdfUrl } from "@/lib/downloadBlob";

const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  pending: "bg-amber-100 text-amber-700",
  submitted: "bg-orange-100 text-orange-700",
  approved: "bg-emerald-100 text-emerald-700",
  open: "bg-blue-100 text-blue-700",
  ordered: "bg-blue-100 text-blue-700",
  acknowledged: "bg-blue-100 text-blue-700", // legacy — displays as Ordered
  shipped: "bg-sky-100 text-sky-700",
  partial: "bg-indigo-100 text-indigo-700",
  received: "bg-emerald-100 text-emerald-700",
  closed: "bg-emerald-100 text-emerald-700", // legacy alias for received
  cancelled: "bg-red-100 text-red-700",
};
// Canonical lifecycle: Draft → Pending → Ordered → (Partial) → Received.
// "Acknowledged" is no longer a distinct status — it's a timestamp on an
// Ordered PO. "open" and "closed" are kept as back-compat aliases only.
const STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending",
  submitted: "Awaiting approval",
  approved: "Approved",
  open: "Ordered",
  ordered: "Ordered",
  acknowledged: "Ordered",
  shipped: "Shipped",
  partial: "Partial",
  received: "Received",
  closed: "Received",
  cancelled: "Cancelled",
};
// Options rendered in the user-facing status dropdown. "Acknowledged" is
// intentionally excluded.
const STATUS_DROPDOWN_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "submitted", label: "Awaiting Approval" },
  { value: "ordered", label: "Ordered" },
  { value: "shipped", label: "Shipped" },
  { value: "partial", label: "Partial" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

function FreightInput({ value, onChange, freightMode, onModeChange }) {
  return (
    <div>
      <label className="text-[10px] text-slate-500 font-medium">Freight</label>
      <div className="flex items-center gap-1">
        <select className="h-7 text-[10px] border rounded px-1 bg-white min-w-[110px]" value={freightMode}
          onChange={e => onModeChange(e.target.value)} data-testid="freight-mode">
          <option value="amount">Enter amount</option>
          <option value="prepaid">Pre-Paid & Add</option>
        </select>
        {freightMode === "amount" ? (
          <NumericFormat value={value || ""} onValueChange={(values) => onChange(values.floatValue || 0)}
            thousandSeparator="," prefix="$" decimalScale={2} allowNegative={false} maxLength={12}
            className="flex h-7 w-28 rounded-md border bg-white px-2 py-1 text-xs text-right shadow-none ring-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-300"
            data-testid="freight-amount" placeholder="$0.00" />
        ) : (
          <span className="text-xs text-emerald-600 font-medium px-2">Pre-Paid</span>
        )}
      </div>
    </div>
  );
}

const PurchaseOrders = forwardRef(function PurchaseOrders({ woId, woNumber, companyInfo, projectName, jobsiteAddress }, ref) {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPO, setExpandedPO] = useState(null);
  // iter273 — Cmd+K deep-link: expand the PO the user picked in the palette.
  useEffect(() => {
    if (!pos.length) return;
    const target = consumeFocusTarget(FOCUS_PO);
    if (!target || !pos.some((p) => p.id === target)) return;
    setExpandedPO(target);
    scrollToTestId(`po-card-${target}`);
  }, [pos]);  const [showCreate, setShowCreate] = useState(null); // null | "upload" | "manual"
  const [saving, setSaving] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [bomData, setBomData] = useState({ items: [], by_manufacturer: {} });
  const [generatingAll, setGeneratingAll] = useState(false);

  useEffect(() => { if (woId) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [woId]);

  const createPOForItem = async (item) => {
      const mfr = manufacturers.find(m => m.name === item.manufacturer);
      const ext = (item.material_cost || 0) * (item.qty || 1);
      try {
        await createPurchaseOrder(woId, {
          po_type: "vendor_line_item",
          manufacturer_id: mfr?.id || null,
          manufacturer_name: item.manufacturer || "",
          vendor_address: mfr?.address || "",
          vendor_contact_name: mfr?.purchasing_contact_name || "",
          vendor_contact_email: mfr?.purchasing_contact_email || "",
          vendor_contact_phone: mfr?.purchasing_contact_phone || "",
          line_items: [{ id: crypto.randomUUID(), description: item.name || "", part_number: item.part_number || "",
            qty: item.qty || 1, units: "ea", unit_cost: item.material_cost || 0, amount: ext, bom_item_id: item.id }],
          subtotal: ext, freight: 0, total: ext,
        });
        toast.success(`PO created for ${item.name}`);
        const loaded = await load();
        // Auto-expand the newest PO
        if (loaded && loaded.length > 0) setExpandedPO(loaded[loaded.length - 1].id);
      } catch { toast.error("Failed to create PO"); }
  };
  const createPOForManufacturer = async (mfrName, items) => {
      const mfr = manufacturers.find(m => m.name === mfrName);
      const lineItems = items.map(i => ({
        id: crypto.randomUUID(), description: i.name || "", part_number: i.part_number || "",
        qty: i.qty || 1, units: "ea", unit_cost: i.material_cost || 0,
        amount: (i.material_cost || 0) * (i.qty || 1), bom_item_id: i.id,
      }));
      const sub = lineItems.reduce((s, li) => s + li.amount, 0);
      try {
        await createPurchaseOrder(woId, {
          po_type: "vendor_line_item",
          manufacturer_id: mfr?.id || null,
          manufacturer_name: mfrName,
          vendor_address: mfr?.address || "",
          vendor_contact_name: mfr?.purchasing_contact_name || "",
          vendor_contact_email: mfr?.purchasing_contact_email || "",
          vendor_contact_phone: mfr?.purchasing_contact_phone || "",
          line_items: lineItems,
          subtotal: sub, freight: 0, total: sub,
        });
        toast.success(`PO created for ${mfrName} (${items.length} items)`);
        const loaded = await load();
        if (loaded && loaded.length > 0) setExpandedPO(loaded[loaded.length - 1].id);
      } catch { toast.error("Failed to create PO"); }
  };
  useImperativeHandle(ref, () => ({ refresh: load, createPOForItem, createPOForManufacturer }));
  const [receivingPO, setReceivingPO] = useState(null);

  const load = async () => {
    try {
      const [poRes, mfrRes, bomRes] = await Promise.all([getPurchaseOrders(woId), getManufacturers(), getBillOfMaterials(woId)]);
      setPOs(poRes.data);
      setManufacturers(mfrRes.data);
      setBomData(bomRes.data);
      window.dispatchEvent(new CustomEvent("abs:po-changed"));
      return poRes.data;
    } catch { toast.error("Failed to load purchase orders"); }
    finally { setLoading(false); }
  };

  const summary = useMemo(() => {
    const total = pos.reduce((s, p) => s + (p.total || 0), 0);
    const pending = pos.filter(p => p.status === "pending").length;
    const open = pos.filter(p => p.status === "open").length;
    const closed = pos.filter(p => p.status === "closed").length;
    return { count: pos.length, total, pending, open, closed };
  }, [pos]);

  const handleStatusChange = async (poId, status) => {
    try {
      await updatePOStatus(woId, poId, status);
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update status"); }
  };

  // SVC-FIX-2 — explicit approval step so a PO is never silently stranded in Draft.
  const handleSubmitForApproval = async (poId) => {
    try {
      const r = await submitPOForApproval(woId, poId);
      toast.success(r.data?.message || "Submitted for approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit the PO for approval"); }
  };
  const handleApprovePO = async (poId) => {
    try {
      const r = await approvePO(woId, poId);
      toast.success(r.data?.message || "PO approved", { duration: 6000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve the PO"); }
  };

  const handleReceiveItems = async (poId, itemIds) => {
    try {
      await receivePOItems(woId, poId, itemIds);
      toast.success("Items marked as received");
      load();
    } catch { toast.error("Failed to receive items"); }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    try {
      const res = await generateAllPOs(woId);
      const created = res.data.created || 0;
      const updated = res.data.updated || 0;
      if (created + updated === 0) {
        toast.info("No pending material items to generate POs for");
      } else {
        const parts = [];
        if (created > 0) parts.push(`${created} new PO${created === 1 ? "" : "s"}`);
        if (updated > 0) parts.push(`${updated} existing PO${updated === 1 ? "" : "s"} updated`);
        toast.success(parts.join(" + "));
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate POs"); }
    finally { setGeneratingAll(false); }
  };

  const bomUnordered = useMemo(() => {
    return (bomData.items || []).filter(i => (i.material_cost || 0) > 0 && i.po_status === "unordered").length;
  }, [bomData]);

  const generatePDF = (po) => {
    const doc = new jsPDF();
    // Unified company header with logo
    const headerY = addCompanyHeader(doc, 10);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("PURCHASE ORDER", 140, headerY + 4);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    // PO Info
    const infoY = headerY + 12;
    doc.text(`PO #: ${po.po_number}`, 14, infoY);
    doc.text(`Date: ${new Date(po.created_at).toLocaleDateString()}`, 14, infoY + 5);
    doc.text(`Project: ${projectName || ""}`, 14, infoY + 10);
    doc.text(`Delivery: ${po.delivery_date || "TBD"}`, 140, infoY);
    if (po.vendor_quote_number) doc.text(`Vendor Quote #: ${po.vendor_quote_number}`, 140, infoY + 5);
    // Vendor & Ship To
    let y = infoY + 18;
    doc.setFont("helvetica", "bold");
    doc.text("VENDOR", 14, y);
    doc.text("SHIP TO", 110, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(po.manufacturer_name || "", 14, y + 5);
    doc.text(po.vendor_address || "", 14, y + 9);
    doc.text(po.vendor_contact_name || "", 14, y + 13);
    doc.text(po.vendor_contact_email || "", 14, y + 17);
    doc.text(po.ship_to_address || jobsiteAddress || "", 110, y + 5);
    // Line items table
    y += 25;
    const rows = po.line_items.map((li, i) => [i + 1, li.part_number || "", li.description, li.qty, li.units || "ea", fmt(li.unit_cost), fmt(li.amount)]);
    autoTable(doc, {
      startY: y,
      head: [["#", "Part #", "Description", "Qty", "Units", "Unit Cost", "Amount"]],
      body: rows,
      theme: "striped",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 25 }, 3: { halign: "center", cellWidth: 15 }, 4: { cellWidth: 15 }, 5: { halign: "right", cellWidth: 25 }, 6: { halign: "right", cellWidth: 25 } },
      margin: { left: 14, right: 14 },
    });
    // Totals
    let totalY = doc.lastAutoTable.finalY + 8;
    const tx = 160;
    doc.setFontSize(9);
    doc.text("Subtotal:", tx - 30, totalY);
    doc.text(fmt(po.subtotal), tx, totalY, { align: "right" });
    totalY += 5;
    doc.text("Freight:", tx - 30, totalY);
    // Pre-Paid & Add — the freight is paid by the vendor up-front and
    // billed back separately, so the PO total excludes it. Render the
    // verbiage instead of $0 so the vendor mirrors the arrangement.
    const isPrepaid = (po.freight_mode || "amount") === "prepaid";
    doc.text(isPrepaid ? "Pre-Paid & Add" : fmt(po.freight), tx, totalY, { align: "right" });
    totalY += 5;
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL:", tx - 30, totalY);
    doc.text(fmt(po.total), tx, totalY, { align: "right" });
    if (po.notes) {
      totalY += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("Notes:", 14, totalY);
      doc.setFont("helvetica", "normal");
      doc.text(po.notes, 14, totalY + 4, { maxWidth: 180 });
    }
    doc.save(`${po.po_number}.pdf`);
  };

  if (loading) return null;

  return (
    <div className="space-y-2" data-testid="wo-purchase-orders">
      {/* Purchase orders tab (UI sweep batch 4) — the tab carries the name; toolbar: actions left, counts right. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="po-toolbar">
        <button type="button" className={btn("write")} onClick={() => setShowCreate("manual")} data-testid="btn-add-po-manual">
          <Plus className="w-3 h-3" /> PO
        </button>
        <button type="button" className={btn("data")} onClick={() => setShowCreate("upload")} data-testid="btn-upload-vendor-quote">
          <Upload className="w-3 h-3" /> Vendor quote
        </button>
        {bomUnordered > 0 && (
          <button type="button" className={btn("write")} onClick={handleGenerateAll} disabled={generatingAll} data-testid="po-generate-all">
            {generatingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}
            Generate all POs ({bomUnordered})
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span className="num">{summary.count} PO{summary.count !== 1 ? "s" : ""}</span>
          <span className="num">{fmt(summary.total)}</span>
          {summary.pending > 0 && <span className="text-amber-600 font-medium num">{summary.pending} pending</span>}
        </div>
      </div>

        <div className="space-y-2">
          {/* Material lines not on a PO yet (replaces the Procurement Log section) */}
          <MaterialToOrder woId={woId} bomData={bomData} manufacturers={manufacturers}
            onCreatePOForItem={createPOForItem} onCreatePOForManufacturer={createPOForManufacturer} onChanged={load} />

          {/* Create form */}
          {showCreate === "upload" && (
            <UploadQuotePOForm woId={woId} manufacturers={manufacturers} onSaved={() => { setShowCreate(null); load(); }} onCancel={() => setShowCreate(null)} />
          )}
          {showCreate === "manual" && (
            <ManualPOForm woId={woId} manufacturers={manufacturers} bomData={bomData} onSaved={() => { setShowCreate(null); load(); }} onCancel={() => setShowCreate(null)} />
          )}

          {/* PO list */}
          {pos.length === 0 && !showCreate && (
            <p className="text-xs text-slate-400 italic py-2">No purchase orders yet. Create one from the material lines above, upload a vendor quote, or add a PO manually.</p>
          )}
          {pos.map(po => (
            <POCard key={po.id} po={po} expanded={expandedPO === po.id}
              onToggle={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
              onStatusChange={(s) => handleStatusChange(po.id, s)}
              onSubmitForApproval={() => handleSubmitForApproval(po.id)}
              onApprove={() => handleApprovePO(po.id)}
              onReceiveItems={(ids) => handleReceiveItems(po.id, ids)}
              onReceive={() => setReceivingPO(po)}
              onDownloadPDF={() => generatePDF(po)}
              onSaved={() => load()}
              woId={woId}
              projectName={projectName}
              jobsiteAddress={jobsiteAddress}
              companyInfo={companyInfo}
            />
          ))}
        </div>
      {receivingPO && <ReceivePOModal po={receivingPO} onClose={() => setReceivingPO(null)} onCheckedIn={() => load()} />}
    </div>
  );
});

export default PurchaseOrders;

const RECEIVABLE = ["approved", "open", "ordered", "shipped", "partial"];

function POCard({ po, expanded, onToggle, onStatusChange, onSubmitForApproval, onApprove, onReceiveItems, onReceive, onDownloadPDF, onSaved, woId, projectName, jobsiteAddress, companyInfo }) {
  const [localPO, setLocalPO] = useState({ ...po });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [showShipment, setShowShipment] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setLocalPO({ ...po }); setDirty(false); }, [po]);

  const update = (changes) => { setLocalPO(prev => ({ ...prev, ...changes })); setDirty(true); };
  // Lump-sum (quoteline) POs keep the quoted subtotal; lines are just the breakout.
  const lump = localPO.po_type === "quoteline";
  const recalc = (items, freight, subtotalOverride) => {
    const sub = lump ? (subtotalOverride ?? localPO.subtotal ?? 0) : items.reduce((s, i) => s + (i.amount || 0), 0);
    return { line_items: items, subtotal: sub, freight: freight ?? localPO.freight ?? 0, total: sub + (freight ?? localPO.freight ?? 0) };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id, po_number, wo_id, created_at, sent_at, received_at, ...payload } = localPO;
      // Strip tax from payload
      delete payload.tax;
      delete payload.tax_amount;
      payload.total = (payload.subtotal || 0) + (payload.freight || 0);
      await updatePurchaseOrder(woId, po.id, payload);
      toast.success("PO updated");
      setDirty(false);
      onSaved();
    } catch { toast.error("Failed to update PO"); }
    finally { setSaving(false); }
  };

  // Persist a patch to the PO (used by Acknowledge modal)
  const onUpdate = async (patch) => {
    try {
      const { id, po_number, wo_id, created_at, ...current } = localPO;
      const merged = { ...current, ...patch };
      delete merged.tax; delete merged.tax_amount;
      merged.total = (merged.subtotal || 0) + (merged.freight || 0);
      await updatePurchaseOrder(woId, po.id, merged);
      setLocalPO(prev => ({ ...prev, ...patch }));
      onSaved();
    } catch (e) {
      toast.error("Failed to save acknowledgment");
      throw e;
    }
  };

  const addLineItem = () => {
    const newItem = { id: crypto.randomUUID(), description: "", part_number: "", qty: 1, units: "ea", unit_cost: 0, amount: 0 };
    const items = [...(localPO.line_items || []), newItem];
    update(recalc(items));
  };

  const handleEmailPO = () => {
    const vendorEmail = po.vendor_contact_email || "";
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const docName = docFilename("Purchase_Order", po.po_number);
    const attach = getEmailDocMode() === "attach";
    const pdfUrl = baseUrl && po.id && woId
      ? `${baseUrl}/api/work-orders/${woId}/purchase-orders/${po.id}/pdf/${docName}`
      : "";
    if (attach && po.id && woId) downloadPdfUrl(`/work-orders/${woId}/purchase-orders/${po.id}/pdf?download=1`, { suggestedName: docName, label: "purchase order" }).catch(() => {});
    const subject = encodeURIComponent(`PO# ${po.po_number} - ${projectName || "Purchase Order"}`);
    const linkLine = attach ? `\n\nThe purchase order (${docName}) is attached.` : pdfUrl ? `\n\nView the purchase order here:\n${pdfUrl}` : "";
    // Item 10 — Pre-Paid & Add freight: call out the billing arrangement
    // explicitly in the email body so the vendor AP team mirrors it on
    // their invoice and there's no surprise freight markup downstream.
    const isPrepaid = (po.freight_mode || "amount") === "prepaid";
    const freightLine = isPrepaid
      ? "\n\nFreight: Pre-Paid & Add — please ship freight pre-paid and bill it back as a separate line on your invoice."
      : "";
    const body = encodeURIComponent(
      `Hi ${po.vendor_contact_name || ""},\n\nPlease find Purchase Order ${po.po_number} with a requested delivery date of ${po.delivery_date || "TBD"}.${linkLine}${freightLine}\n\nThank you.`
    );
    window.open(`mailto:${vendorEmail}?subject=${subject}&body=${body}`, "_blank");
    // Auto-transition pending → open when emailing
    if (po.status === "pending") {
      onStatusChange("open");
    }
  };

  // UI sweep batch 4 — every per-PO action lives behind the row's "⋯" menu; the row itself is read-only
  // apart from the status pill. Conditions are unchanged from the old inline buttons.
  const notDone = po.status !== "cancelled" && !["received", "closed"].includes(po.status);
  const canSubmit = (po.status === "draft" || po.status === "pending" || !po.status) && !po.is_stock_po;
  const menu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          title="Actions" data-testid={`po-menu-${po.id}`}>
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 text-xs" data-testid={`po-menu-content-${po.id}`}>
        {canSubmit && (
          <DropdownMenuItem onClick={onSubmitForApproval} data-testid={`po-submit-approval-${po.id}`}>Submit for Approval</DropdownMenuItem>
        )}
        {po.status === "submitted" && (
          <DropdownMenuItem onClick={onApprove} className="text-emerald-700" data-testid={`po-approve-${po.id}`}><CheckCircle className="w-3.5 h-3.5 mr-2" />Approve</DropdownMenuItem>
        )}
        {RECEIVABLE.includes(po.status) && (
          <DropdownMenuItem onClick={onReceive} data-testid={`po-receive-${po.id}`}><Package className="w-3.5 h-3.5 mr-2" />Receive…</DropdownMenuItem>
        )}
        {(canSubmit || po.status === "submitted" || RECEIVABLE.includes(po.status)) && <DropdownMenuSeparator />}
        <DropdownMenuItem onClick={() => setShowPreview(true)} data-testid={`btn-view-po-${po.id}`}><Eye className="w-3.5 h-3.5 mr-2" />View</DropdownMenuItem>
        <DropdownMenuItem onClick={onDownloadPDF} data-testid={`btn-pdf-po-${po.id}`}><FileDown className="w-3.5 h-3.5 mr-2" />PDF</DropdownMenuItem>
        <DropdownMenuItem onClick={handleEmailPO} data-testid={`btn-email-po-${po.id}`}><Mail className="w-3.5 h-3.5 mr-2" />Email PO</DropdownMenuItem>
        <DropdownMenuSeparator />
        {notDone && !po.acknowledged_at && (
          <DropdownMenuItem onClick={() => setShowAckModal(true)} title="Record vendor acknowledgement + ship date" data-testid={`btn-ack-po-${po.id}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-2" />Acknowledge
          </DropdownMenuItem>
        )}
        {notDone && po.acknowledged_at && (
          <DropdownMenuItem onClick={() => setShowAckModal(true)} className="text-slate-500" title={`Acknowledged ${po.acknowledged_at.slice(0, 10)}${po.vendor_ship_date ? ` — ships ${po.vendor_ship_date}` : ""}`} data-testid={`po-edit-ack-${po.id}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-2" />Edit Acknowledgement
          </DropdownMenuItem>
        )}
        {notDone && (
          <DropdownMenuItem onClick={() => setShowShipment(true)} title="Ship date, ETA, tracking number" data-testid={`btn-shipment-po-${po.id}`}>
            <Truck className="w-3.5 h-3.5 mr-2" />Tracking / ETA
          </DropdownMenuItem>
        )}
        {["received", "closed"].includes(po.status) && (
          <DropdownMenuItem onClick={onReceive} title="Receipt history per line item" data-testid={`btn-receive-edit-po-${po.id}`}>
            <Package className="w-3.5 h-3.5 mr-2" />Receipts
          </DropdownMenuItem>
        )}
        {po.status === "cancelled" && (
          <DropdownMenuItem onClick={() => onStatusChange("draft")} data-testid={`btn-reopen-po-${po.id}`}><RotateCcw className="w-3.5 h-3.5 mr-2" />Reopen as Draft</DropdownMenuItem>
        )}
        {po.status !== "closed" && po.status !== "cancelled" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onStatusChange("cancelled")} className="text-red-600 focus:text-red-700" data-testid={`btn-cancel-po-${po.id}`}><X className="w-3.5 h-3.5 mr-2" />Cancel PO</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="border rounded-lg bg-white" data-testid={`po-card-${po.id}`}>
      <div className="px-3 py-2 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2 w-full text-left">
          <button className="flex items-center gap-2 flex-1 min-w-0 text-left" onClick={onToggle} data-testid={`po-toggle-${po.id}`}>
            {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
            <span className="text-sm font-mono font-medium text-slate-800">{po.po_number}</span>
            <span className="text-xs text-slate-600 truncate max-w-[220px]" title={po.manufacturer_name}>{po.manufacturer_name || "—"}</span>
            {po.po_type === "quoteline" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">Quoteline</span>}
          </button>
          {/* Status dropdown — canonical lifecycle (no "Acknowledged").
              Acknowledgement is shown as a small green tick next to the
              badge whenever `acknowledged_at` is set on an Ordered PO. */}
          {(() => {
            const canonical = (po.status === "open" || po.status === "acknowledged") ? "ordered"
              : po.status === "closed" ? "received"
              : (po.status || "draft");
            const isAcked = !!po.acknowledged_at;
            const chipDate = isAcked
              ? (po.vendor_ship_date || po.acknowledged_at?.slice(0, 10) || "Ack'd")
              : (localPO.delivery_date || "No date");
            return (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${STATUS_COLORS[canonical] || "bg-slate-100"}`}
                      data-testid={`po-status-badge-${po.id}`}
                      title="Click to change status">
                      {STATUS_LABELS[canonical] || canonical}
                      {isAcked && (canonical === "ordered") && (
                        <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                      )}
                      <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" data-testid={`po-status-menu-${po.id}`}>
                    {STATUS_DROPDOWN_OPTIONS.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => onStatusChange(opt.value)}
                        data-testid={`po-status-option-${opt.value}-${po.id}`}>
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className={`text-[10px] num ${isAcked ? "text-emerald-700 font-medium" : "text-slate-400"}`}>{chipDate}</span>
                {(po.eta || po.tracking_number) && (
                  <span className="hidden items-center gap-1.5 text-[10px] text-slate-500 sm:inline-flex" data-testid={`po-shipment-chip-${po.id}`}>
                    {po.eta && <span className="num">ETA {shortDate(po.eta)}</span>}
                    {po.tracking_number && <TrackingLink po={po} testId={`po-tracking-${po.id}`} />}
                  </span>
                )}
              </>
            );
          })()}
          {po.status === "submitted" && (
            <span className="text-[10px] text-amber-700" data-testid={`po-approval-reason-${po.id}`}>{po.approval_reason || "awaiting PM approval"}</span>
          )}
          {(po.status === "approved" || po.foundation_sync) && (
            <FoundationSyncPill sync={po.foundation_sync || { status: "ready", message: "Approved — accounting sends it from Settings › Integrations › Foundation › Sync" }} testId={`po-foundation-sync-${po.id}`} />
          )}
          {(() => {
            const lines = (po.line_items || []).filter(li => (li.amount || 0) > 0 || (li.qty || 0) > 0);
            const got = lines.filter(li => li.received).length;
            return RECEIVABLE.includes(po.status) || po.status === "received" ? (
              <span className={`text-[10px] num ${got === lines.length && lines.length ? "text-emerald-700" : "text-slate-500"}`} data-testid={`po-received-progress-${po.id}`}>{got}/{lines.length} lines in</span>
            ) : null;
          })()}
          <span className="text-xs font-medium num text-slate-700 ml-auto">{fmt(po.total)}</span>
          {menu}
        </div>
      </div>

      {expanded && (
        <div className="border-t px-3 py-2 space-y-3">
          {po.vendor_quote_number && <p className="text-xs text-slate-500">Vendor Quote #: <span className="font-medium text-slate-700">{po.vendor_quote_number}</span></p>}

          {/* Inline-editable line items — column widths tuned so Unit Cost
              and Amount always fit values like $1,234.56 without scroll. */}
          <div className="border rounded overflow-hidden">
            <div className="grid grid-cols-[24px_90px_1fr_52px_44px_104px_96px_48px] gap-1 px-2 py-1 text-[10px] text-slate-500 font-semibold bg-slate-50">
              <div>#</div><div>Part #</div><div>Description</div><div className="text-center">Qty</div><div>Units</div><div className="text-right">Unit cost</div><div className="text-right">Amount</div><div className="text-center">Recv</div>
            </div>
            {(localPO.line_items || []).map((li, idx) => (
              <div key={li.id} className={`grid grid-cols-[24px_90px_1fr_52px_44px_104px_96px_48px] gap-1 px-2 py-1 text-xs border-t border-slate-100 items-center ${li.received ? "bg-emerald-50/50" : ""}`} data-testid={`po-line-item-${li.id}`}>
                <div className="text-slate-400">{idx + 1}</div>
                <Input className="h-6 text-xs border-transparent hover:border-slate-200 focus:border-blue-300 bg-transparent" value={li.part_number || ""}
                  onChange={e => { const items = [...localPO.line_items]; items[idx] = { ...items[idx], part_number: e.target.value }; update({ line_items: items }); }} />
                <Input className="h-6 text-xs border-transparent hover:border-slate-200 focus:border-blue-300 bg-transparent" value={li.description}
                  onChange={e => { const items = [...localPO.line_items]; items[idx] = { ...items[idx], description: e.target.value }; update({ line_items: items }); }} />
                <Input type="number" className="h-6 text-xs text-center border-transparent hover:border-slate-200 focus:border-blue-300 bg-transparent" value={li.qty}
                  onChange={e => { const items = [...localPO.line_items]; const q = parseFloat(e.target.value) || 0; items[idx] = { ...items[idx], qty: q, amount: q * items[idx].unit_cost }; update(recalc(items)); }} />
                <Input className="h-6 text-xs border-transparent hover:border-slate-200 focus:border-blue-300 bg-transparent" value={li.units || "ea"}
                  onChange={e => { const items = [...localPO.line_items]; items[idx] = { ...items[idx], units: e.target.value }; update({ line_items: items }); }} />
                {lump ? <div className="text-right text-[10px] text-slate-400">lump</div> : (
                <MoneyInput className="border-transparent hover:border-slate-200 focus:border-blue-300 shadow-none" value={li.unit_cost} testId={`po-line-unit-cost-${li.id}`}
                  onChange={uc => { const items = [...localPO.line_items]; items[idx] = { ...items[idx], unit_cost: uc, amount: items[idx].qty * uc }; update(recalc(items)); }} />
                )}
                <div className="text-right tabular-nums font-medium">{lump ? "—" : fmt(li.amount)}</div>
                <div className="text-center flex items-center justify-center gap-1">
                  {li.received ? (
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (li.received_qty || 0) > 0 ? (
                    <button className="text-[10px] tabular-nums text-indigo-700 hover:underline" onClick={onReceive} title={`${li.received_qty} of ${li.qty} received — click to receive more`} data-testid={`po-line-partial-${li.id}`}>
                      {li.received_qty}/{li.qty}
                    </button>
                  ) : li.amount > 0 && RECEIVABLE.includes(po.status) ? (
                    <button className="text-slate-400 hover:text-emerald-500" onClick={() => onReceiveItems([li.id])} title={`Mark all ${remainingQty(li)} received`} data-testid={`btn-receive-item-${li.id}`}>
                      <Package className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button className="text-slate-300 hover:text-red-400" onClick={() => {
                      const items = localPO.line_items.filter((_, i) => i !== idx);
                      update(recalc(items));
                    }}><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add line item + Freight + Delivery */}
          <div className="flex items-end gap-3 flex-wrap">
            <button type="button" className={btn("mode")} onClick={addLineItem} data-testid={`btn-add-line-${po.id}`}>
              <Plus className="w-3 h-3" /> Part
            </button>
            <div className="flex-1" />
            <div className="flex flex-col gap-1.5 items-end">
              <FreightInput
                value={localPO.freight || 0}
                onChange={f => update(recalc(localPO.line_items, f))}
                freightMode={localPO.freight_mode || "amount"}
                onModeChange={mode => {
                  const f = mode === "prepaid" ? 0 : localPO.freight || 0;
                  update({ freight_mode: mode, ...recalc(localPO.line_items, f) });
                }}
              />
              <div className="w-full">
                <label className="text-[10px] text-slate-500 font-medium">Delivery date</label>
                <Input type="date" className="h-7 text-xs" value={localPO.delivery_date || ""} data-testid={`input-delivery-${po.id}`}
                  onChange={e => update({ delivery_date: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Ship To — editable; blank falls back to the job-site address */}
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-slate-500 font-medium">Ship to</label>
              <button type="button" className="text-[9px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                onClick={() => update({ ship_to_address: jobsiteAddress || "" })} data-testid={`btn-shipto-jobsite-${po.id}`}>
                Job Site
              </button>
              <button type="button" className="text-[9px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                onClick={async () => {
                  try {
                    const res = await getSettings();
                    const sl = res.data?.shop_location || {};
                    const addr = [sl.address, sl.city, sl.state, sl.zip].filter(Boolean).join(", ");
                    update({ ship_to_address: addr || "1785 W 160th Ave, Broomfield, CO, 80023" });
                  } catch { toast.error("Couldn't load shop address"); }
                }} data-testid={`btn-shipto-shop-${po.id}`}>
                Shop / Warehouse
              </button>
            </div>
            <Input className="h-7 text-xs mt-1" placeholder={jobsiteAddress ? `Default: ${jobsiteAddress}` : "Street, City, State, Zip"}
              value={localPO.ship_to_address || ""} onChange={e => update({ ship_to_address: e.target.value })}
              data-testid={`input-shipto-${po.id}`} />
          </div>

          {/* Totals (no tax) */}
          <div className="flex justify-end mt-3">
            <div className="text-xs space-y-0.5 min-w-[180px]">
              {lump ? (
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Quote total</span>
                  <MoneyInput className="w-28 bg-white" value={localPO.subtotal ?? 0} testId={`input-lump-sum-${po.id}`}
                    onChange={v => update(recalc(localPO.line_items, undefined, v))} /></div>
              ) : (
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(localPO.subtotal)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-slate-500">Freight</span><span className="tabular-nums">{localPO.freight_mode === "prepaid" ? "Pre-Paid" : fmt(localPO.freight)}</span></div>
              <div className="flex justify-between font-semibold border-t pt-0.5"><span>Total</span><span className="tabular-nums">{fmt(localPO.total)}</span></div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium">Notes</label>
            <textarea className="w-full text-xs border rounded p-1.5 h-12 resize-none border-transparent hover:border-slate-200 focus:border-blue-300 bg-transparent"
              value={localPO.notes || ""} onChange={e => update({ notes: e.target.value })} data-testid={`input-notes-${po.id}`} />
          </div>

          {/* Save — the only inline action; everything else is in the row's "⋯" menu */}
          {dirty && (
            <div className="flex items-center justify-end pt-1 border-t border-slate-100">
              <button type="button" className={btn("write")} onClick={handleSave} disabled={saving} data-testid={`btn-save-po-${po.id}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals live outside the expanded body — the "⋯" menu opens them from a collapsed row too. */}
      {showPreview && <POPreviewModal po={localPO} projectName={projectName} jobsiteAddress={jobsiteAddress} companyInfo={companyInfo} onClose={() => setShowPreview(false)} />}
      {showAckModal && <POAcknowledgeModal po={localPO} onSave={async (patch) => {
        // Route ack data through the dedicated PATCH endpoint so
        // the full payload (date, notes, attachment) persists —
        // the generic PUT uses a Pydantic schema that would strip
        // these extra fields.
        try {
          const resp = await acknowledgePurchaseOrder(woId, po.id, {
            vendor_ship_date: patch.vendor_ship_date,
            ack_notes: patch.ack_notes || "",
            ack_document: patch.ack_document || null,
          });
          setLocalPO(prev => ({ ...prev, ...resp }));
          onSaved();
          setShowAckModal(false);
          toast.success("Acknowledgment saved");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Failed to save acknowledgment");
        }
      }} onClose={() => setShowAckModal(false)} />}
      {showShipment && (
        <ShipmentDialog po={localPO} onClose={() => setShowShipment(false)}
          onSaved={(fresh) => { if (fresh) setLocalPO(prev => ({ ...prev, ...fresh })); onSaved(); }} />
      )}
    </div>
  );
}

// Upload Vendor Quote PO — supports both "Vendor Line Item" and "Quoteline" modes
function UploadQuotePOForm({ woId, manufacturers, onSaved, onCancel }) {
  const [step, setStep] = useState("upload"); // upload | choose_type | review
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [poType, setPOType] = useState(null); // "vendor_line_item" | "quoteline"
  const [form, setForm] = useState({ manufacturer_name: "", vendor_quote_number: "", line_items: [], freight: 0, subtotal: 0, total: 0, notes: "", description: "" });
  const [freightMode, setFreightMode] = useState("amount");
  const [saving, setSaving] = useState(false);

  const handleParse = async () => {
    if (!file) { toast.error("Select a file first"); return; }
    setParsing(true);
    try {
      const res = await parseVendorQuote(woId, file);
      setParsedData(res.data);
      setStep("choose_type");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to parse quote"); }
    finally { setParsing(false); }
  };

  const applyParsedData = (type) => {
    setPOType(type);
    const d = parsedData;
    if (type === "vendor_line_item") {
      const items = (d.line_items || []).map(li => ({
        id: crypto.randomUUID(), description: li.description || "", part_number: li.part_number || "",
        qty: li.qty || 1, units: "ea", unit_cost: li.unit_cost || 0, amount: li.amount || (li.qty || 1) * (li.unit_cost || 0),
      }));
      const sub = items.reduce((s, i) => s + i.amount, 0);
      setForm({ manufacturer_name: d.vendor_name || "", vendor_quote_number: d.quote_number || "", line_items: items,
        freight: d.freight || 0, subtotal: sub, total: sub + (d.freight || 0), notes: "", description: "" });
    } else {
      // Quoteline: one quoted total; the vendor's lines become the unpriced breakout so receiving can be per line.
      const total = d.total || d.subtotal || 0;
      const breakout = (d.line_items || []).filter(li => li.description).map(li => ({
        id: crypto.randomUUID(), description: li.description || "", part_number: li.part_number || "", qty: li.qty || 1, units: li.units || "ea", unit_cost: 0, amount: 0,
      }));
      setForm({ manufacturer_name: d.vendor_name || "", vendor_quote_number: d.quote_number || "",
        description: breakout.map(li => li.description).join("; ") || `Per Vendor Quote #${d.quote_number || ""}`,
        freight: d.freight || 0, subtotal: total, total: total + (d.freight || 0), notes: "",
        line_items: breakout,
      });
    }
    setStep("review");
  };

  const recalcTotals = (items, freight) => {
    const sub = items.reduce((s, i) => s + (i.amount || 0), 0);
    return { subtotal: sub, total: sub + (freight || 0) };
  };

  const handleSave = async () => {
    if (!form.manufacturer_name) { toast.error("Select a vendor"); return; }
    setSaving(true);
    try {
      const mfr = manufacturers.find(m => m.name === form.manufacturer_name);
      const payload = {
        po_type: poType, manufacturer_id: mfr?.id || null, manufacturer_name: form.manufacturer_name,
        vendor_quote_number: form.vendor_quote_number,
        vendor_address: mfr?.address || "", vendor_contact_name: mfr?.purchasing_contact_name || "",
        vendor_contact_email: mfr?.purchasing_contact_email || "", vendor_contact_phone: mfr?.purchasing_contact_phone || "",
        line_items: poType === "quoteline" && !(form.line_items || []).some(li => li.description)
          ? [{ id: crypto.randomUUID(), description: form.description || `Per Vendor Quote #${form.vendor_quote_number}`, qty: 1, units: "lot", unit_cost: form.subtotal, amount: form.subtotal }]
          : form.line_items,
        subtotal: form.subtotal, freight: freightMode === "prepaid" ? 0 : form.freight,
        total: form.subtotal + (freightMode === "prepaid" ? 0 : form.freight),
        notes: [form.notes, freightMode === "prepaid" ? "Freight Pre-Paid and Add" : ""].filter(Boolean).join("\n"),
        ship_via: freightMode === "prepaid" ? "Freight Pre-Paid" : "",
      };
      await createPurchaseOrder(woId, payload);
      toast.success(`${poType === "quoteline" ? "Quoteline" : "Vendor Line Item"} PO created`);
      onSaved();
    } catch { toast.error("Failed to create PO"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border rounded bg-blue-50/50 p-3 space-y-2" data-testid="upload-quote-po-form">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-slate-700">Upload vendor quote</h5>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Upload a vendor quote (PDF or image) to auto-extract data.</p>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="text-xs" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="upload-quote-file" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleParse} disabled={!file || parsing} data-testid="upload-quote-parse">
              {parsing ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Parsing...</> : <><Upload className="w-3 h-3 mr-1" /> Parse Quote</>}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Choose type */}
      {step === "choose_type" && parsedData && (
        <div className="space-y-3">
          <div className="text-xs text-slate-600 bg-white rounded border p-2">
            <p><span className="font-medium">Vendor:</span> {parsedData.vendor_name || "Unknown"}</p>
            <p><span className="font-medium">Quote #:</span> {parsedData.quote_number || "N/A"}</p>
            <p><span className="font-medium">Items found:</span> {(parsedData.line_items || []).length}</p>
            <p><span className="font-medium">Total:</span> {fmt(parsedData.total)}</p>
          </div>
          <p className="text-xs font-medium text-slate-700">How would you like to create this PO?</p>
          <div className="grid grid-cols-2 gap-2">
            <button className="border rounded p-3 text-left hover:bg-blue-50 hover:border-blue-300 transition-colors" onClick={() => applyParsedData("vendor_line_item")} data-testid="choose-vendor-line-item">
              <div className="text-sm font-semibold text-slate-800">Vendor Line Item PO</div>
              <p className="text-[10px] text-slate-500 mt-1">Each line item from the quote becomes a separate PO row with qty, unit cost, and extended amount.</p>
            </button>
            <button className="border rounded p-3 text-left hover:bg-violet-50 hover:border-violet-300 transition-colors" onClick={() => applyParsedData("quoteline")} data-testid="choose-quoteline">
              <div className="text-sm font-semibold text-slate-800">Quoteline PO</div>
              <p className="text-[10px] text-slate-500 mt-1">Single line: vendor, quote #, description, qty 1 at the total quote amount plus freight.</p>
            </button>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStep("upload")}>Back</Button>
        </div>
      )}

      {/* Step 3: Review & edit */}
      {step === "review" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full font-medium ${poType === "quoteline" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
              {poType === "quoteline" ? "Quoteline PO" : "Vendor Line Item PO"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 font-medium">Vendor *</label>
              <select className="w-full h-7 text-xs border rounded px-2 bg-white" value={form.manufacturer_name}
                onChange={e => setForm(p => ({ ...p, manufacturer_name: e.target.value }))} data-testid="upload-po-manufacturer">
                <option value="">Select...</option>
                {manufacturers.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                {form.manufacturer_name && !manufacturers.find(m => m.name === form.manufacturer_name) && (
                  <option value={form.manufacturer_name}>{form.manufacturer_name} (from quote)</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-medium">Vendor Quote #</label>
              <Input className="h-7 text-xs" value={form.vendor_quote_number} onChange={e => setForm(p => ({ ...p, vendor_quote_number: e.target.value }))} data-testid="upload-po-quote-number" />
            </div>
          </div>

          {/* Quoteline: show description + total fields */}
          {poType === "quoteline" && (
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-slate-500 font-medium">Description</label>
                <Input className="h-7 text-xs" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="upload-po-description" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 font-medium">Quote Total ($)</label>
                  <MoneyInput className="h-7 bg-white" value={form.subtotal} testId="upload-po-total" onChange={v => {
                    setForm(p => ({ ...p, subtotal: v, total: v + (freightMode === "prepaid" ? 0 : p.freight || 0) }));
                  }} />
                </div>
                <FreightInput value={form.freight} onChange={f => {
                  setForm(p => ({ ...p, freight: f, total: (p.subtotal || 0) + f }));
                }} freightMode={freightMode} onModeChange={setFreightMode} />
              </div>
              <div className="text-xs text-right font-semibold tabular-nums">Grand Total: {fmt(form.total)}</div>
              {/* Breakout — what's in the lump sum, so the warehouse can receive line by line. No prices here. */}
              <div className="space-y-1" data-testid="quoteline-breakout">
                <div className="grid grid-cols-[90px_1fr_50px_50px_24px] gap-1 text-[10px] text-slate-500 font-semibold px-1">
                  <div>Part #</div><div>Breakout item</div><div className="text-center">Qty</div><div>UOM</div><div></div>
                </div>
                {(form.line_items || []).map((li, idx) => (
                  <div key={li.id} className="grid grid-cols-[90px_1fr_50px_50px_24px] gap-1 items-center" data-testid={`quoteline-breakout-row-${idx}`}>
                    <Input className="h-6 text-xs" value={li.part_number || ""} onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], part_number: e.target.value }; setForm(p => ({ ...p, line_items: items })); }} />
                    <Input className="h-6 text-xs" value={li.description} onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], description: e.target.value }; setForm(p => ({ ...p, line_items: items })); }} />
                    <Input type="number" className="h-6 text-xs text-center" value={li.qty} onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], qty: parseFloat(e.target.value) || 0 }; setForm(p => ({ ...p, line_items: items })); }} />
                    <Input className="h-6 text-xs" value={li.units || "ea"} onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], units: e.target.value }; setForm(p => ({ ...p, line_items: items })); }} />
                    <button className="text-red-400 hover:text-red-600" onClick={() => setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }))}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" data-testid="quoteline-add-breakout" onClick={() => setForm(p => ({ ...p, line_items: [...(p.line_items || []), { id: crypto.randomUUID(), description: "", part_number: "", qty: 1, units: "ea", unit_cost: 0, amount: 0 }] }))}>
                  <Plus className="w-3 h-3 mr-1" /> Add breakout line
                </Button>
              </div>
            </div>
          )}

          {/* Vendor Line Item: show editable line items */}
          {poType === "vendor_line_item" && (
            <div className="space-y-1">
              <div className="grid grid-cols-[90px_1fr_50px_100px_90px_24px] gap-1 text-[10px] text-slate-500 font-semibold px-1">
                <div>Part #</div><div>Description</div><div className="text-center">Qty</div><div className="text-right">Unit cost</div><div className="text-right">Amount</div><div></div>
              </div>
              {form.line_items.map((li, idx) => (
                <div key={li.id} className="grid grid-cols-[90px_1fr_50px_100px_90px_24px] gap-1 items-center">
                  <Input className="h-6 text-xs" value={li.part_number || ""} onChange={e => {
                    const items = [...form.line_items]; items[idx] = { ...items[idx], part_number: e.target.value };
                    setForm(p => ({ ...p, line_items: items }));
                  }} />
                  <Input className="h-6 text-xs" value={li.description} onChange={e => {
                    const items = [...form.line_items]; items[idx] = { ...items[idx], description: e.target.value };
                    setForm(p => ({ ...p, line_items: items }));
                  }} />
                  <Input type="number" className="h-6 text-xs text-center" value={li.qty} onChange={e => {
                    const items = [...form.line_items]; const q = parseFloat(e.target.value) || 0;
                    items[idx] = { ...items[idx], qty: q, amount: q * items[idx].unit_cost };
                    const t = recalcTotals(items, form.freight);
                    setForm(p => ({ ...p, line_items: items, ...t }));
                  }} />
                  <MoneyInput className="bg-white" value={li.unit_cost} testId={`quoteline-unit-cost-${idx}`} onChange={uc => {
                    const items = [...form.line_items];
                    items[idx] = { ...items[idx], unit_cost: uc, amount: items[idx].qty * uc };
                    const t = recalcTotals(items, form.freight);
                    setForm(p => ({ ...p, line_items: items, ...t }));
                  }} />
                  <span className="text-xs text-right tabular-nums font-medium">{fmt(li.amount)}</span>
                  <button className="text-red-400 hover:text-red-600" onClick={() => {
                    const items = form.line_items.filter((_, i) => i !== idx);
                    const t = recalcTotals(items, form.freight);
                    setForm(p => ({ ...p, line_items: items, ...t }));
                  }}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => {
                const items = [...form.line_items, { id: crypto.randomUUID(), description: "", part_number: "", qty: 1, units: "ea", unit_cost: 0, amount: 0 }];
                setForm(p => ({ ...p, line_items: items }));
              }}><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
              <div className="grid grid-cols-2 gap-2">
                <FreightInput value={form.freight} onChange={f => {
                    const t = recalcTotals(form.line_items, f);
                    setForm(p => ({ ...p, freight: f, ...t }));
                  }} freightMode={freightMode} onModeChange={setFreightMode} />
                <div className="flex items-end">
                  <div className="text-xs w-full">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(form.subtotal)}</span></div>
                    <div className="flex justify-between font-semibold"><span>Total</span><span className="tabular-nums">{fmt(form.total)}</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div><label className="text-[10px] text-slate-500 font-medium">Notes</label>
            <textarea className="w-full text-xs border rounded p-1.5 h-12 resize-none" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          <div className="flex justify-between">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStep("choose_type")}>Back</Button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="upload-po-save">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Create PO
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Manual Add PO — vendor + part dropdowns from Procurement Log
function ManualPOForm({ woId, manufacturers, bomData, onSaved, onCancel }) {
  const [form, setForm] = useState({
    manufacturer_name: "", line_items: [], freight: 0, tax: 0, subtotal: 0, total: 0, notes: "", vendor_quote_number: "",
  });
  const [freightMode, setFreightMode] = useState("amount"); // amount | prepaid
  const [saving, setSaving] = useState(false);

  const bomManufacturers = useMemo(() => {
    const mfrs = new Set();
    (bomData.items || []).forEach(i => { if (i.manufacturer) mfrs.add(i.manufacturer); });
    return Array.from(mfrs).sort();
  }, [bomData]);

  // Parts filtered by selected vendor — show only material items
  const availableParts = useMemo(() => {
    const items = (bomData.items || []).filter(i => (i.material_cost || 0) > 0);
    if (!form.manufacturer_name) return items;
    return items.filter(i => i.manufacturer === form.manufacturer_name);
  }, [form.manufacturer_name, bomData]);

  const allManufacturers = useMemo(() => {
    const names = new Set(manufacturers.map(m => m.name));
    bomManufacturers.forEach(n => names.add(n));
    return Array.from(names).sort();
  }, [manufacturers, bomManufacturers]);

  const recalcTotals = (items, freight) => {
    const sub = items.reduce((s, i) => s + (i.amount || 0), 0);
    const f = freight || 0;
    return { subtotal: sub, total: sub + f };
  };

  const addBomItem = (bomItem) => {
    if (form.line_items.find(li => li.bom_item_id === bomItem.id)) { toast.info("Item already added"); return; }
    const li = {
      id: crypto.randomUUID(), description: bomItem.name || "", part_number: bomItem.part_number || "",
      qty: bomItem.qty || 1, units: "ea", unit_cost: bomItem.material_cost || 0,
      amount: (bomItem.qty || 1) * (bomItem.material_cost || 0), bom_item_id: bomItem.id,
    };
    const items = [...form.line_items, li];
    const t = recalcTotals(items, form.freight);
    setForm(p => ({ ...p, line_items: items, ...t }));
  };

  const addAllForMfr = () => {
    const unordered = availableParts.filter(p => p.po_status === "unordered" && !form.line_items.find(li => li.bom_item_id === p.id));
    if (unordered.length === 0) { toast.info("No unordered items to add"); return; }
    const newItems = unordered.map(p => ({
      id: crypto.randomUUID(), description: p.name || "", part_number: p.part_number || "",
      qty: p.qty || 1, units: "ea", unit_cost: p.material_cost || 0,
      amount: (p.qty || 1) * (p.material_cost || 0), bom_item_id: p.id,
    }));
    const items = [...form.line_items, ...newItems];
    const t = recalcTotals(items, form.freight);
    setForm(p => ({ ...p, line_items: items, ...t }));
    toast.success(`Added ${newItems.length} items`);
  };

  const handleSave = async () => {
    if (!form.manufacturer_name) { toast.error("Select a vendor"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const mfr = manufacturers.find(m => m.name === form.manufacturer_name);
      const freight = freightMode === "prepaid" ? 0 : form.freight;
      const freightNote = freightMode === "prepaid" ? "Freight Pre-Paid and Add" : "";
      const notes = [form.notes, freightNote].filter(Boolean).join("\n");
      await createPurchaseOrder(woId, {
        po_type: "vendor_line_item", manufacturer_id: mfr?.id || null, manufacturer_name: form.manufacturer_name,
        vendor_quote_number: form.vendor_quote_number,
        vendor_address: mfr?.address || "", vendor_contact_name: mfr?.purchasing_contact_name || "",
        vendor_contact_email: mfr?.purchasing_contact_email || "", vendor_contact_phone: mfr?.purchasing_contact_phone || "",
        line_items: form.line_items, subtotal: form.subtotal, freight,
        total: form.subtotal + freight, notes, ship_via: freightMode === "prepaid" ? "Freight Pre-Paid" : "",
      });
      toast.success("PO created");
      onSaved();
    } catch { toast.error("Failed to create PO"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border rounded bg-slate-50 p-3 space-y-2" data-testid="manual-po-form">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-slate-700">Add PO</h5>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 font-medium">Vendor *</label>
          <select className="w-full h-7 text-xs border rounded px-2 bg-white" value={form.manufacturer_name}
            onChange={e => setForm(p => ({ ...p, manufacturer_name: e.target.value }))} data-testid="manual-po-manufacturer">
            <option value="">Select...</option>
            {allManufacturers.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 font-medium">Customer PO / Reference #</label>
          <Input className="h-7 text-xs" value={form.vendor_quote_number} onChange={e => setForm(p => ({ ...p, vendor_quote_number: e.target.value }))} data-testid="manual-po-ref" />
        </div>
      </div>

      {/* Add from BOM — shows name + part# + cost */}
      {availableParts.length > 0 && (
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[10px] text-slate-500 font-medium">Add from Procurement Log</label>
            {form.manufacturer_name && availableParts.filter(p => p.po_status === "unordered").length > 0 && (
              <button className="text-[10px] text-violet-600 font-medium hover:underline" onClick={addAllForMfr} data-testid="manual-po-add-all-mfr">
                Add All Unordered ({availableParts.filter(p => p.po_status === "unordered" && !form.line_items.find(li => li.bom_item_id === p.id)).length})
              </button>
            )}
          </div>
          <div className="border rounded bg-white divide-y max-h-40 overflow-auto mt-1">
            {availableParts.map(part => {
              const added = form.line_items.find(li => li.bom_item_id === part.id);
              return (
                <button key={part.id} disabled={!!added}
                  className={`w-full text-left px-2 py-1.5 flex items-center gap-2 text-xs hover:bg-blue-50 transition-colors ${added ? "opacity-40" : ""}`}
                  onClick={() => addBomItem(part)} data-testid={`bom-add-${part.id}`}>
                  <span className="font-medium text-slate-700 truncate flex-1">{part.name}</span>
                  <span className="text-slate-400 text-[10px] min-w-[60px]">{part.part_number || "-"}</span>
                  <span className="text-slate-500 text-[10px] min-w-[20px] text-center">{part.qty}</span>
                  <span className="tabular-nums text-slate-600 min-w-[70px] text-right">{fmt(part.material_cost)}</span>
                  <span className={`text-[10px] px-1 py-0.5 rounded ${
                    part.po_status === "ordered" ? "bg-blue-100 text-blue-600" :
                    part.po_status === "received" ? "bg-emerald-100 text-emerald-600" :
                    "bg-amber-100 text-amber-600"}`}>
                    {added ? "Added" : part.po_status === "ordered" ? "Ordered" : part.po_status === "received" ? "Recv" : "Unord"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Line items */}
      <div className="space-y-1">
        <div className="grid grid-cols-[90px_1fr_50px_100px_90px_24px] gap-1 text-[10px] text-slate-500 font-semibold px-1">
          <div>Part #</div><div>Description</div><div className="text-center">Qty</div><div className="text-right">Unit cost</div><div className="text-right">Amount</div><div></div>
        </div>
        {form.line_items.map((li, idx) => (
          <div key={li.id} className="grid grid-cols-[90px_1fr_50px_100px_90px_24px] gap-1 items-center">
            <Input className="h-6 text-xs" value={li.part_number || ""} onChange={e => {
              const items = [...form.line_items]; items[idx] = { ...items[idx], part_number: e.target.value };
              setForm(p => ({ ...p, line_items: items }));
            }} />
            <Input className="h-6 text-xs" value={li.description} onChange={e => {
              const items = [...form.line_items]; items[idx] = { ...items[idx], description: e.target.value };
              setForm(p => ({ ...p, line_items: items }));
            }} />
            <Input type="number" className="h-6 text-xs text-center" value={li.qty} onChange={e => {
              const items = [...form.line_items]; const q = parseFloat(e.target.value) || 0;
              items[idx] = { ...items[idx], qty: q, amount: q * items[idx].unit_cost };
              const t = recalcTotals(items, form.freight);
              setForm(p => ({ ...p, line_items: items, ...t }));
            }} />
            <MoneyInput className="bg-white" value={li.unit_cost} testId={`po-create-unit-cost-${idx}`} onChange={uc => {
              const items = [...form.line_items];
              items[idx] = { ...items[idx], unit_cost: uc, amount: items[idx].qty * uc };
              const t = recalcTotals(items, form.freight);
              setForm(p => ({ ...p, line_items: items, ...t }));
            }} />
            <span className="text-xs text-right tabular-nums font-medium">{fmt(li.amount)}</span>
            <button className="text-red-400 hover:text-red-600" onClick={() => {
              const items = form.line_items.filter((_, i) => i !== idx);
              const t = recalcTotals(items, form.freight);
              setForm(p => ({ ...p, line_items: items, ...t }));
            }}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => {
          const items = [...form.line_items, { id: crypto.randomUUID(), description: "", part_number: "", qty: 1, units: "ea", unit_cost: 0, amount: 0 }];
          setForm(p => ({ ...p, line_items: items }));
        }} data-testid="manual-po-add-line"><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
      </div>

      {/* Freight & Totals */}
      <div className="grid grid-cols-2 gap-2">
        <FreightInput value={form.freight} onChange={f => {
          const t = recalcTotals(form.line_items, f);
          setForm(p => ({ ...p, freight: f, ...t }));
        }} freightMode={freightMode} onModeChange={setFreightMode} />
        <div className="flex items-end">
          <div className="text-xs w-full">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(form.subtotal)}</span></div>
            <div className="flex justify-between font-semibold"><span>Total</span><span className="tabular-nums">{fmt(form.total)}</span></div>
          </div>
        </div>
      </div>
      <div><label className="text-[10px] text-slate-500 font-medium">Notes</label>
        <textarea className="w-full text-xs border rounded p-1.5 h-12 resize-none" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="manual-po-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Create PO
        </Button>
      </div>
    </div>
  );
}


function POPreviewModal({ po, projectName, jobsiteAddress, companyInfo, onClose }) {
  const ci = companyInfo || {};
  const lineSubtotal = (po.line_items || []).reduce((s, li) => s + (li.amount || 0), 0);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-testid="po-preview-modal">
      <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[90vh] overflow-auto p-8 relative" onClick={e => e.stopPropagation()}>
        <button className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" onClick={onClose} data-testid="po-preview-close"><X className="w-5 h-5" /></button>

        {/* Company header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-bold text-sm text-slate-800">{ci.company_name || COMPANY_INFO.name}</div>
            <div className="text-[11px] text-slate-500">{ci.address || COMPANY_INFO.fullAddress}</div>
            <div className="text-[11px] text-slate-500">{ci.phone || COMPANY_INFO.phone}</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-slate-800 tracking-wide">PURCHASE ORDER</div>
          </div>
        </div>

        {/* PO info row */}
        <div className="grid grid-cols-2 gap-4 text-xs text-slate-700 mb-4 border-t pt-3">
          <div className="space-y-1">
            <div><span className="font-semibold">PO #:</span> {po.po_number}</div>
            <div><span className="font-semibold">Date:</span> {po.created_at ? new Date(po.created_at).toLocaleDateString() : "—"}</div>
            <div><span className="font-semibold">Project:</span> {projectName || "—"}</div>
          </div>
          <div className="space-y-1 text-right">
            <div><span className="font-semibold">Delivery:</span> {po.delivery_date || "TBD"}</div>
            {po.vendor_quote_number && <div><span className="font-semibold">Vendor Quote #:</span> {po.vendor_quote_number}</div>}
          </div>
        </div>

        {/* Vendor & Ship To */}
        <div className="grid grid-cols-2 gap-6 text-xs mb-4 border-t pt-3">
          <div>
            <div className="font-bold text-slate-600 uppercase text-[10px] mb-1">Vendor</div>
            <div className="font-medium text-slate-800">{po.manufacturer_name || "—"}</div>
            {po.vendor_address && <div className="text-slate-500">{po.vendor_address}</div>}
            {po.vendor_contact_name && <div className="text-slate-500">{po.vendor_contact_name}</div>}
            {po.vendor_contact_email && <div className="text-slate-500">{po.vendor_contact_email}</div>}
          </div>
          <div>
            <div className="font-bold text-slate-600 uppercase text-[10px] mb-1">Ship To</div>
            <div className="text-slate-700">{po.ship_to_address || jobsiteAddress || "—"}</div>
          </div>
        </div>

        {/* Line items table */}
        <table className="w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="bg-slate-700 text-white text-[10px] uppercase">
              <th className="py-1.5 px-2 text-left w-8">#</th>
              <th className="py-1.5 px-2 text-left w-24">Part #</th>
              <th className="py-1.5 px-2 text-left">Description</th>
              <th className="py-1.5 px-2 text-center w-12">Qty</th>
              <th className="py-1.5 px-2 text-center w-12">Units</th>
              <th className="py-1.5 px-2 text-right w-20">Unit Cost</th>
              <th className="py-1.5 px-2 text-right w-20">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(po.line_items || []).map((li, idx) => (
              <tr key={li.id} className="border-b border-slate-100">
                <td className="py-1.5 px-2 text-slate-400">{idx + 1}</td>
                <td className="py-1.5 px-2 text-slate-500 font-mono">{li.part_number || ""}</td>
                <td className="py-1.5 px-2 font-medium text-slate-700">{li.description}</td>
                <td className="py-1.5 px-2 text-center tabular-nums">{li.qty}</td>
                <td className="py-1.5 px-2 text-center text-slate-500">{li.units || "ea"}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{fmt(li.unit_cost)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{fmt(li.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="text-xs space-y-1 min-w-[200px]">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(po.subtotal || lineSubtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Freight</span><span className="tabular-nums">{(po.freight_mode || "amount") === "prepaid" ? "Pre-Paid & Add" : fmt(po.freight)}</span></div>
            <div className="flex justify-between font-bold text-sm border-t pt-1"><span>TOTAL</span><span className="tabular-nums">{fmt(po.total)}</span></div>
          </div>
        </div>

        {po.notes && (
          <div className="mt-4 border-t pt-3">
            <div className="text-[10px] font-bold text-slate-600 uppercase mb-1">Notes</div>
            <div className="text-xs text-slate-600 whitespace-pre-wrap">{po.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}


// ============== PO ACKNOWLEDGE MODAL ==============
// Manual capture of vendor acknowledgment while we wait for Graph inbound email automation.
// Saves: acknowledged_at, vendor_ship_date, ack_document (base64 PDF if uploaded)
function POAcknowledgeModal({ po, onSave, onClose }) {
  const [shipDate, setShipDate] = useState(po.vendor_ship_date || "");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!shipDate) { toast.error("Ship date required"); return; }
    setSaving(true);
    try {
      const patch = {
        acknowledged_at: new Date().toISOString(),
        vendor_ship_date: shipDate,
        ack_notes: notes,
      };
      if (file) {
        const b64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        patch.ack_document = { filename: file.name, content_type: file.type, data: b64 };
      }
      await onSave(patch);
    } catch { /* toast handled in onSave */ }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="po-ack-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Acknowledge PO {po.po_number}</h3>
        <p className="text-xs text-slate-500 mb-4">Record the vendor's acknowledgement + expected ship date.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 font-medium">Vendor Ship Date <span className="text-red-500">*</span></label>
            <Input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)} className="h-8 text-sm mt-1" data-testid="ack-ship-date" />
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Notes (optional)</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g., Vendor confirmed 2-week lead time" className="h-8 text-sm mt-1" data-testid="ack-notes" />
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Acknowledgement PDF (optional)</label>
            <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files?.[0] || null)} className="h-8 text-xs mt-1 file:mr-2 file:py-0 file:px-2 file:text-xs file:bg-slate-100 file:border-0" data-testid="ack-file" />
            {file && <div className="text-[10px] text-emerald-600 mt-1">Selected: {file.name}</div>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSave} disabled={saving} data-testid="ack-save">
            {saving ? "Saving..." : "Save Acknowledgment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
